import { CalendarOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Table, Tag, Tooltip, Typography } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserDisplayName } from "../../common/utils/userDisplayName";
import type { TechnicianAssignmentSchedule } from "../generated-axios-client";
import type { RootState } from "../store";
import {
  bookingStatusLabel,
  confirmCancelBooking,
  isBookingCancellable,
  isBookingEditable,
  STATUS_TAG_COLOR,
  shownBookingStatus,
} from "./bookingStatus";
import { EditBookingDatesModal } from "./EditBookingDatesModal";
import { formatBookingRange } from "../../common/utils/formatBookingRange";
import { ScheduleTechniciansModal } from "./ScheduleTechniciansModal";

type Props = {
  intl: IntlShape;
  ticketId: string;
  ticketNumber: number | null | undefined;
  ticketStatus: string | null | undefined;
  modalApi: HookAPI | undefined;
};

export const AssignmentSchedulesInfo = ({
  intl,
  ticketId,
  ticketNumber,
  ticketStatus,
  modalApi,
}: Props) => {
  const dispatch = useDispatch();
  const schedules = useSelector(
    (state: RootState) => state.assignmentSchedule.ticketSchedules
  );
  const technicians = useSelector(
    (state: RootState) => state.assignmentSchedule.technicians
  );
  const [bookingOpen, setBookingOpen] = useState(false);
  const [editedBooking, setEditedBooking] = useState<TechnicianAssignmentSchedule | null>(
    null
  );
  const closeBooking = useCallback(() => setBookingOpen(false), []);
  const closeEdit = useCallback(() => setEditedBooking(null), []);
  const source = useMemo(() => ({ ticketId }), [ticketId]);
  const workOrders = useMemo(
    () => [{ id: ticketId, ticketNumber }],
    [ticketId, ticketNumber]
  );

  const technicianNames = useMemo(
    () =>
      Object.fromEntries(
        (technicians ?? []).map((technician) => [
          technician.id,
          getUserDisplayName(technician),
        ])
      ) as Record<number, string>,
    [technicians]
  );

  const columns: ColumnsType<TechnicianAssignmentSchedule> = [
    {
      key: "technician",
      title: intl.formatMessage({ id: "schedule_column_technician" }),
      render: (_, booking) =>
        technicianNames[booking.technician] ?? `#${booking.technician}`,
    },
    {
      key: "period",
      title: intl.formatMessage({ id: "schedule_modal_period" }),
      render: (_, booking) =>
        formatBookingRange(intl, booking.start_date, booking.end_date),
    },
    {
      key: "status",
      title: intl.formatMessage({ id: "schedule_column_status" }),
      render: (_, booking) => {
        const statusTag = (
          <Tag color={STATUS_TAG_COLOR[shownBookingStatus(booking)]}>
            {bookingStatusLabel(intl, booking)}
          </Tag>
        );
        return booking.status === "SKIPPED" && booking.skip_reason ? (
          <Tooltip
            title={intl.formatMessage({
              id: `schedule_skip_reason_${booking.skip_reason}`,
            })}
          >
            {statusTag}
          </Tooltip>
        ) : (
          statusTag
        );
      },
    },
    {
      key: "actions",
      align: "right",
      render: (_, booking) => (
        <Flex gap="small" justify="flex-end">
          {isBookingEditable(booking) && (
            <Button size="small" onClick={() => setEditedBooking(booking)}>
              {intl.formatMessage({ id: "schedule_edit_dates" })}
            </Button>
          )}
          {isBookingCancellable(booking) && (
            <Button
              size="small"
              danger
              onClick={() =>
                confirmCancelBooking({
                  booking,
                  technicianName: technicianNames[booking.technician] ?? "",
                  intl,
                  modalApi,
                  dispatch,
                  source,
                })
              }
            >
              {intl.formatMessage({ id: "schedule_cancel" })}
            </Button>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" gap="small" wrap>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "schedule_tab_hint" })}
        </Typography.Text>
        <Button
          type="primary"
          icon={<CalendarOutlined />}
          disabled={ticketStatus === "DONE"}
          onClick={() => setBookingOpen(true)}
        >
          {intl.formatMessage({ id: "schedule_technicians" })}
        </Button>
      </Flex>
      <Table<TechnicianAssignmentSchedule>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={schedules ?? []}
        loading={schedules === null}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "schedule_empty" }) }}
      />
      <ScheduleTechniciansModal
        open={bookingOpen}
        intl={intl}
        workOrders={workOrders}
        source={source}
        onClose={closeBooking}
      />
      <EditBookingDatesModal
        intl={intl}
        booking={editedBooking}
        source={source}
        onClose={closeEdit}
      />
    </Flex>
  );
};
